import ParameterValidator from '@mvps-genesis/parameter-validator';
import { MenuBar, MenuItem } from '@mvps-genesis/accessibility';
import { getAbsoluteParent, getNextSibling, getPreviousSibling, getParent } from '@mvps-genesis/selector-helpers';
import { createFocusTrap } from 'focus-trap';

/* eslint-disable no-invalid-this */
export default class NavigationMenu {

	/**
	 * Options:
	 *
	 * options.drawers
	 * options.mainMenu
	 * options.mainMenuOpenButtons
	 * options.mainMenuCloseButtons
	 * options.menuItems
	 * options.parentMenus
	 * options.showPreviousMenuButtons
	 */
	constructor(options) {

		// Create validator
		this._validator = new ParameterValidator(this);

		this.options = this._validator.isObject('options', options);
		this.breakpoint = this._validator.isString('options.breakpoint', options.breakpoint || '60em');

		this.elements = {
			mainMenu: this._validator.isElement('options.mainMenu', options.mainMenu),
			mainMenuCloseButtons: this._validator.isElementList('options.mainMenuCloseButtons', options.mainMenuCloseButtons),
			mainMenuOpenButtons: this._validator.isElementList('options.mainMenuOpenButtons', options.mainMenuOpenButtons),
			parentMenus: this._validator.isElementList('options.parentMenus', options.parentMenus),
			showPreviousMenuButtons: this._validator.isElementList('options.showPreviousMenuButtons', options.showPreviousMenuButtons)
		};
		this.elements.menuItems = this._validator.isElementList('options.menuItems', options.menuItems, { container: this.elements.mainMenu });
		this.elements.parentMenuItems = this._validator.isElementList('parentMenuItems', `${options.parentMenus} > ${options.menuItems}`, { container: this.elements.mainMenu });

		this.accessibility = {
			// Create focus trap for mobile menu
			focusTrap: createFocusTrap(this.elements.mainMenu, {
				escapeDeactivates: false,
				setReturnFocus: this.elements.mainMenuOpenButtons[0]
			}),
			// Create accessible (keyboard navigation) menu from the main NavigationMenu element
			menuBar: new MenuBar({ element: this.elements.mainMenu })
		};

		this.updateNavigationMenuOpenState();

		this.bindEvents();

	}

	isMobile() {
		// mobile < 75em
		return this.breakpoint ? window.matchMedia(`(max-width: ${this.breakpoint})`).matches : false;
	}

	bindEvents(add = true) {

		const method = add ? 'addEventListener' : 'removeEventListener';

		// Add listener for top-level close
		this.accessibility.menuBar[method](MenuBar.events.close, (e) => this.onAccessibilityMenuClose(e), true);

		// Mobile open and close events
		for (const mainMenuOpenButton of this.elements.mainMenuOpenButtons) {

			// Use the new mainMenuToggle function to handle both open and close
			mainMenuOpenButton[method]('click', this.mainMenuToggle, false);

		}
		for (const mainMenuCloseButton of this.elements.mainMenuCloseButtons) {

			mainMenuCloseButton[method]('click', this.mainMenuClose, false);

		}

		for (const parentMenuItem of this.elements.parentMenuItems) {

			parentMenuItem[method]('click', this.onParentMenuItemClick, false);
			parentMenuItem[method](MenuBar.events.enterSubMenu, this.onParentMenuItemClick, false);

		}

		// Show previous button
		for (const showPreviousMenuButton of this.elements.showPreviousMenuButtons) {

			showPreviousMenuButton[method]('click', this.onPreviousButtonClick, false);

		}

	}

	destroyEvents() {
		this.bindEvents(false);
	}

	mainMenuOpen = () => {

		document.documentElement.classList.add('has-open-main-menu');
		this.elements.mainMenu.classList.add('is-open');
		this.elements.mainMenu.classList.remove('is-hidden');

		this.accessibility.menuBar.focusFirstMenuItem();
	}

	mainMenuClose = () => {

		document.documentElement.classList.remove('has-open-main-menu');
		this.elements.mainMenu.classList.remove('is-open');
		this.elements.mainMenu.classList.add('is-hidden');

		this.accessibility.menuBar.resetMenuItemsState();

		// Deactivate focus trap for mobile menu
		if (this.isMobile()) {
			this.accessibility.focusTrap.deactivate();
		}

	}

	mainMenuToggle = () => {
		if (this.elements.mainMenu.classList.contains('is-open')) {
			this.mainMenuClose();
		} else {
			this.mainMenuOpen();
		}
	}

	mainMenuToggle = () => {
		if (this.elements.mainMenu.classList.contains('is-open')) {
			this.mainMenuClose();
		} else {
			this.mainMenuOpen();
		}
	}

	onAccessibilityMenuClose(e) {

		if (e && e.detail && e.detail.menu) {
			if (e.detail.menu.root) {
				this.mainMenuClose();
			}
			else {
				const previousButton = e.detail.menu.element.querySelector(this.options.showPreviousMenuButtons);
				this.showPreviousMenu(previousButton);
			}

		}

		return true;

	}

	onParentMenuItemClick = (e) => {

		e.stopPropagation();

		e.preventDefault();

		let clickElement = e.target;
		// this is for the case when the click is on a <span> element within <a> link
		const parentElement = getParent(clickElement, this.options.menuItems);
		if (parentElement) {
			clickElement = parentElement;
		}

		// get .js-navigation-menu__parent-toplevel parent element
		const directParentElement = getParent(clickElement);

		// toggle clicked element for arrow animation
		clickElement.classList.toggle('is-open');

		// toggle the drawer element for second level menu and third level menu
		const nextDrawer = getNextSibling(clickElement, this.options.drawers);
		if (nextDrawer) {
			nextDrawer.classList.toggle('is-open');
		}

		// if the clicked element is a toplevel parent menu, close all other toplevel parent menus
		if (
			directParentElement &&
			directParentElement.classList.contains('js-navigation-menu__parent-toplevel')
		) {
			Array.from(directParentElement.parentNode.children).forEach((sibling) => {
				if (sibling !== directParentElement) {
					Array.from(sibling.children).forEach((child) => {
						child.classList.remove('is-open');
					});
				}
			});
		}

		this.updateNavigationMenuOpenState();

		return false;

	}

	onPreviousButtonClick = (e) => {

		e.stopPropagation();
		e.preventDefault();

		const parentDrawer = getParent(e.target, this.options.drawers);
		parentDrawer.classList.remove('is-open');
		const parentMenuItem = getPreviousSibling(parentDrawer, this.options.menuItems);
		parentMenuItem.classList.remove('is-open');


		// Properly handle correct focusing on "back"
		const menuItem = MenuItem.getInstance(e.target);
		if (menuItem) {
			menuItem.dispatchEvent('keydown', {
				key: 'Escape'
			});
		}

		this.updateNavigationMenuOpenState();

	}

	showPreviousMenu(target) {
		const parentDrawer = getParent(target, this.options.drawers);
		if (parentDrawer) {
			parentDrawer.classList.remove('is-open');
		}
	}

	updateNavigationMenuOpenState() {
		// Check if any of the secondary menus are open
		const navMenu = document.getElementById('navigation_menu');
		const links = document.querySelectorAll('.g-navigation-menu__drawer');
		const hasOpen = Array.from(links).some(link => link.classList.contains('is-open'));

		if (navMenu) {
			if (hasOpen) {
				navMenu.classList.add('has-secondary-menu-open');

			} else {
				navMenu.classList.remove('has-secondary-menu-open');
			}
		}
	}

};
